import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Building } from 'lucide-react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import SEO, { SEO_CONFIGS } from '../../components/SEO';

const TermsConditions = () => {
  return (
    <MarketingLayout lightTheme>
      <SEO {...SEO_CONFIGS.terms} />
      <section className="pt-32 pb-20 bg-gradient-to-b from-slate-50 to-white min-h-screen">
        <div className="container mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>

            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-slate-900">Terms & Conditions</h1>
            <p className="text-slate-500 mb-8">Last updated: January 2026</p>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
              <p className="text-slate-600 mb-8 leading-relaxed">
                These Terms govern access to and use of Nanokart.ai services.
              </p>

              <div className="space-y-8">
                {/* Section 1 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
                  <p className="text-slate-600">
                    By accessing or using Nanokart.ai services, you agree to these Terms. 
                    If you do not agree, do not use the Services.
                  </p>
                </div>

                {/* Section 2 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">2. Description of Services</h2>
                  <p className="text-slate-600 mb-4">Nanokart.ai provides AI-powered APIs including:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Virtual Try-On image generation</li>
                    <li>Moments (lifestyle scene generation)</li>
                    <li>Partner Dashboard & analytics</li>
                    <li>CDN-based image delivery</li>
                  </ul>
                </div>

                {/* Section 3 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">3. Account Registration & Security</h2>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Accurate information is required during registration</li>
                    <li>You are responsible for safeguarding API keys and credentials</li>
                    <li>You are liable for all activity under your account</li>
                  </ul>
                </div>

                {/* Section 4 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">4. API Usage & Credits</h2>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>API usage is metered via Nanokart Credits</li>
                    <li>Pricing is published on the website and may change with notice</li>
                    <li>Cached outputs may be free or discounted</li>
                    <li>Unused credits are non-refundable</li>
                  </ul>
                </div>

                {/* Section 5 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">5. Acceptable Use</h2>
                  <p className="text-slate-600 mb-4">You may not:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Upload content violating IP or privacy rights</li>
                    <li>Upload unlawful, offensive, or harmful content</li>
                    <li>Reverse-engineer models or systems</li>
                    <li>Resell or share API access</li>
                    <li>Abuse rate limits or platform safeguards</li>
                  </ul>
                </div>

                {/* Section 6 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">6. AI Output Disclaimer</h2>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Outputs are AI-generated and illustrative</li>
                    <li>Results may vary and are not guaranteed to reflect real-world fit, appearance, or outcomes</li>
                    <li>Nanokart makes no guarantees regarding business performance or customer satisfaction</li>
                  </ul>
                </div>

                {/* Section 7 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">7. Intellectual Property</h2>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>You retain ownership of uploaded images</li>
                    <li>You may use generated outputs commercially</li>
                    <li>Nanokart retains all rights to its models, software, and infrastructure</li>
                  </ul>
                </div>

                {/* Section 8 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">8. Privacy & Data Use</h2>
                  <p className="text-slate-600">
                    Use of data is governed by our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>. 
                    Images are not used for training without explicit consent.
                  </p>
                </div>

                {/* Section 9 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">9. Service Availability</h2>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Target uptime: 99.9%</li>
                    <li>Maintenance and updates may occur</li>
                    <li>No liability for interruptions beyond reasonable control</li>
                  </ul>
                </div>

                {/* Section 10 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">10. Limitation of Liability</h2>
                  <p className="text-slate-600 mb-4">To the maximum extent permitted by law:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Nanokart is not liable for indirect or consequential damages</li>
                    <li>Total liability is limited to fees paid in the preceding 12 months</li>
                  </ul>
                </div>

                {/* Section 11 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">11. Indemnification</h2>
                  <p className="text-slate-600 mb-4">You agree to indemnify Nanokart against claims arising from:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Uploaded content</li>
                    <li>IP violations</li>
                    <li>Misuse of outputs or Services</li>
                  </ul>
                </div>

                {/* Section 12 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">12. Termination</h2>
                  <p className="text-slate-600 mb-4">Nanokart may suspend or terminate accounts for:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Policy violations</li>
                    <li>Fraud or abuse</li>
                    <li>Legal requirements</li>
                  </ul>
                  <p className="text-slate-600 mt-4">
                    Unused credits may be forfeited only in cases of violation or abuse.
                  </p>
                </div>

                {/* Section 13 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">13. Governing Law & Jurisdiction</h2>
                  <p className="text-slate-600">
                    These Terms are governed by the laws of India. Courts of Bangalore, Karnataka have exclusive jurisdiction.
                  </p>
                </div>

                {/* Section 14 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">14. Changes to Terms</h2>
                  <p className="text-slate-600">
                    Material changes will be notified via email or dashboard. Continued use constitutes acceptance.
                  </p>
                </div>

                {/* Section 15 - Contact */}
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">15. Contact</h2>
                  <div className="flex flex-col sm:flex-row gap-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Email</p>
                        <a href="mailto:legal@nanokart.ai" className="text-blue-600 font-medium hover:underline">
                          legal@nanokart.ai
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Building className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Company</p>
                        <p className="text-slate-700 font-medium">NanoKart India Private Limited, Bangalore, India</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default TermsConditions;
